/** @jsxImportSource @emotion/react */
import contoursThumbnail from '@app/assets/images/contours-basemap-thumbnail.png'
import satelliteThumbnail from '@app/assets/images/satellite-basemap-thumbnail.png'
import streetThumbnail from '@app/assets/images/street-basemap-thumbnail.png'
import { useTheme } from '@mui/material/styles'
import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useEffect, useState } from 'react'

import { useMap } from '@app/contexts/ModuleMapContexts/RiskMapContext'
import { Box, Typography } from '@mui/material'
import { Icon } from '@app/components/Atoms/Icon'
import {
  baseMapButton,
  baseMapButtonContainer,
  baseMapTitleWrapper,
  previewContainer,
  previewMap,
  previewMapContainer,
} from './BaseMapButton.styles'
import { usePreferences } from '@app/hooks/usePreferences'

export const BaseMapButton = () => {
  const theme = useTheme()
  const [baseMapIndex, setBaseMapIndex] = useState(0)
  const [showPreview, setShowPreview] = useState(false)
  const { basemap_list } = usePreferences()
  const { basemap, changeMapStyle, setBasemap, map, interactive } = useMap()

  const getThumbnail = (type: string) => {
    switch (type) {
      case 'satellite':
        return satelliteThumbnail
      case 'contours':
        return contoursThumbnail
      case 'street':
      default:
        return streetThumbnail
    }
  }

  const cycleBaseMap = useCallback(() => {
    if (baseMapIndex < basemap_list.length - 1) {
      setBasemap(basemap_list[baseMapIndex + 1].label)
    } else {
      setBasemap(basemap_list[0].label)
    }
  }, [basemap_list, baseMapIndex, setBasemap])

  // Reset the base map style if the basemap list changes
  // Try to match up, if not, default to the first one
  useEffect(() => {
    if (!map || !basemap_list || basemap_list.length === 0) return
    const mapStyleSprite = map.getStyle()?.sprite
    if (!mapStyleSprite) return
    // Extract the value after 'urbanintelligence' or 'mapbox' up to either a forward slash or a '?'
    const spriteMatch = mapStyleSprite.match(/(?:urbanintelligence|mapbox)\/([^/?]*)/)
    const sourceMatchIndex = basemap_list.findIndex((bMap) => {
      const match = bMap.source.match(/(?:urbanintelligence|mapbox)\/([^/?]*)/)
      return match && spriteMatch && match[1] === spriteMatch[1]
    })
    if (spriteMatch && sourceMatchIndex !== -1) return
    // const indexNum = sourceMatchIndex !== -1 ? sourceMatchIndex : 0
    // try to find similar style using basemap label
    const indexNum = basemap_list.findIndex((bMap) => bMap.label === basemap) || 0

    // if a flyto is in progress, wait for it to finish before changing the style
    // otherwise the map changes and the flyto is cancelled
    // assume the map is flying already
    map.once('moveend', () => {
      setTimeout(() => {
        changeMapStyle({ style: basemap_list[indexNum].source })
        setBaseMapIndex(indexNum)
        setShowPreview(false)
      }, 0)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basemap_list, basemap])

  useEffect(() => {
    const index = basemap_list.findIndex((map) => map.label === basemap)
    if (index !== -1) {
      setBaseMapIndex(index)
      changeMapStyle({ style: basemap_list[index].source })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basemap])

  if (!basemap_list || basemap_list.length === 0) {
    return null
  }

  return interactive ? (
    <Box
      css={baseMapButtonContainer({ theme })}
      onMouseEnter={() => setShowPreview(true)}
      onMouseLeave={() => setShowPreview(false)}
      className="tutorial-basemap"
    >
      <Box
        component="button"
        onClick={cycleBaseMap}
        css={baseMapButton({
          thumbnail: getThumbnail(basemap_list[baseMapIndex].type),
          theme,
        })}
      >
        <Box css={baseMapTitleWrapper({ theme })}>
          <Icon iconName="Layers" size="medium" colour={theme.palette.white.main} />
          <Typography variant="body1" color={theme.palette.white.main}>
            View
          </Typography>
        </Box>
      </Box>
      <AnimatePresence>
        {showPreview && (
          <motion.div
            css={previewContainer({ theme })}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {basemap_list.map((map, index) => {
              const isSelected = index === baseMapIndex

              return (
                <Box
                  key={map.label}
                  css={previewMapContainer({ theme })}
                  onClick={() => {
                    setBaseMapIndex(index)
                    changeMapStyle({ style: basemap_list[index].source })
                  }}
                >
                  <Box
                    component="span"
                    css={previewMap({ theme, isSelected, thumbnail: getThumbnail(map.type) })}
                  />
                  <Typography variant="body2">{map.label}</Typography>
                </Box>
              )
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  ) : null
}
